/**
 * TrialCountdown & PromoBanner — paired widgets shown inside the Website
 * Builder layout.
 *
 *   • PromoBanner    — skinny strip across the top: "Get your first 3 months
 *                      for $9.99 ($3.33/month)". Dismissible per session.
 *   • TrialCountdown — bottom-left floating box. Shows the time-left clock
 *                      while in trial; once a plan is picked, shrinks to a
 *                      compact "Standard · Active" pill.
 *
 * Both pull from GET /api/builder/plan-status.
 * PlanPickerDialog (this file too) handles plan selection.
 */
import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Sparkles, X, ArrowRight, Clock, Check, Crown, Zap, Loader2,
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function usePlanStatus() {
  const { getAuthHeaders } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/builder/plan-status`, { headers: getAuthHeaders() });
      setData(r.data);
    } catch {
      // Silent — widgets just hide on failure
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => { reload(); }, [reload]);
  return { data, loading, reload };
}

/* ─────────────────────────────────────────────────────────────
 *  PromoBanner — slim top strip
 * ─────────────────────────────────────────────────────────── */
export function PromoBanner({ planStatus, onPickPlan }) {
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem('promoBannerDismissed') === '1');
  if (!planStatus?.promo?.eligible || dismissed) return null;
  return (
    <div
      className="relative w-full text-white text-xs px-4 py-2 flex items-center justify-center gap-3"
      style={{ background: 'linear-gradient(90deg, #8B5CF6 0%, #EC4899 50%, #F59E0B 100%)' }}
      data-testid="promo-banner"
    >
      <Sparkles size={14} className="shrink-0" />
      <p className="font-semibold tracking-wide">
        Get your first 3 months for <span className="underline">$9.99</span> ($3.33/month)
      </p>
      <button
        onClick={onPickPlan}
        className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-white/15 hover:bg-white/30 font-bold uppercase tracking-wider text-[10px] transition-all"
        data-testid="promo-select-plan"
      >
        Select a plan <ArrowRight size={11} />
      </button>
      <button
        onClick={() => { sessionStorage.setItem('promoBannerDismissed', '1'); setDismissed(true); }}
        className="absolute right-2 top-1/2 -translate-y-1/2 text-white/70 hover:text-white p-1"
        aria-label="Dismiss"
        data-testid="promo-dismiss"
      >
        <X size={13} />
      </button>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
 *  TrialCountdown — bottom-left floating box
 * ─────────────────────────────────────────────────────────── */
export function TrialCountdown({ planStatus, onPickPlan, inline = false }) {
  if (!planStatus?.has_site) return null;

  const selectedPlan = planStatus.selected_plan;
  // Inline mode renders without fixed positioning so the widget can sit
  // INSIDE the sidebar just above the user footer. Non-inline keeps the
  // legacy floating bottom-left behavior.
  const wrap = inline
    ? ''
    : 'fixed bottom-4 left-[276px] z-40';

  // After they pick a plan, shrink to compact pill
  if (selectedPlan) {
    return (
      <div
        className={`${wrap} inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider shadow-[0_10px_28px_rgba(0,0,0,0.4)] border border-white/[0.08] ${inline ? 'w-full justify-center' : ''}`}
        style={{
          background: selectedPlan.id === 'premium'
            ? 'linear-gradient(135deg, #F59E0B, #D97706)'
            : 'linear-gradient(135deg, #8B5CF6, #6D28D9)',
          color: '#fff',
        }}
        data-testid="plan-active-badge"
      >
        {selectedPlan.id === 'premium' ? <Crown size={13} /> : <Zap size={13} />}
        {selectedPlan.name} · Active
      </div>
    );
  }

  const expired = planStatus.trial_expired;
  const daysLeft = planStatus.trial_days_remaining;
  const inTrial = planStatus.in_trial;

  // Pre-trial (free play): friendly nudge instead of countdown
  if (!inTrial && !expired) {
    return (
      <button
        onClick={onPickPlan}
        className={`${wrap} ${inline ? 'w-full' : 'w-[220px]'} text-left bg-gradient-to-br from-[#15152E] to-[#0E0E23] border border-white/[0.08] rounded-2xl p-3 shadow-[0_18px_50px_rgba(0,0,0,0.55)] hover:border-[#A78BFA]/40 hover:shadow-[0_18px_50px_rgba(139,92,246,0.35)] transition-all`}
        data-testid="trial-countdown-play-mode"
      >
        <p className="text-[9px] uppercase tracking-[0.18em] text-[#A78BFA] font-bold mb-1">Free to explore</p>
        <p className="text-white text-sm font-bold font-syne leading-tight">Publishing? Pick a plan.</p>
        <p className="text-[10px] text-white/55 mt-1">Build for free — pay when you launch.</p>
      </button>
    );
  }

  // Trial / expired
  const ramp = expired ? '#FB7185' : daysLeft <= 3 ? '#F59E0B' : '#A3E635';
  // Iter 191c — Inline (in-sidebar) variant is now a SLIM SINGLE-ROW CHIP so
  // it stops covering the Settings sidebar entry. Full card layout stays for
  // the floating (non-inline) usage.
  if (inline) {
    return (
      <button
        onClick={onPickPlan}
        className="w-full flex items-center justify-between gap-2 px-3 py-1.5 rounded-full bg-white/[0.05] border border-white/10 hover:bg-white/[0.10] hover:border-[#A78BFA]/50 transition-all group"
        data-testid="trial-countdown"
      >
        <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.14em]" style={{ color: ramp }}>
          <Clock size={10} />
          {expired ? 'Trial ended' : `${daysLeft}d left`}
        </span>
        <span className="inline-flex items-center gap-1 text-[9.5px] font-bold uppercase tracking-[0.14em] text-[#A78BFA] group-hover:text-white transition-colors">
          Select a plan <ArrowRight size={10} />
        </span>
      </button>
    );
  }
  return (
    <div
      className={`${wrap} w-[260px] bg-gradient-to-br from-[#15152E] to-[#0E0E23] border border-white/[0.08] rounded-2xl p-4 shadow-[0_18px_50px_rgba(0,0,0,0.55)]`}
      data-testid="trial-countdown"
    >
      <div className="flex items-center justify-between gap-2 mb-2">
        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] font-bold" style={{ color: ramp }}>
          <Clock size={11} /> {expired ? 'Trial ended' : 'Trial · time left'}
        </span>
      </div>
      <p className="text-3xl font-bold text-white font-syne tabular-nums" data-testid="trial-days-left">
        {expired ? '0' : daysLeft}
        <span className="text-sm font-medium text-white/55 ml-1">
          {expired ? 'days' : daysLeft === 1 ? 'day left' : 'days left'}
        </span>
      </p>
      <button
        onClick={onPickPlan}
        className="mt-3 w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-gradient-to-r from-[#8B5CF6] to-[#6D28D9] text-white text-[11px] font-bold uppercase tracking-wider hover:shadow-[0_8px_24px_rgba(139,92,246,0.55)] transition-all"
        data-testid="trial-select-plan"
      >
        Select a plan <ArrowRight size={12} />
      </button>
      {!expired && planStatus.promo?.eligible && (
        <p className="text-[10px] text-[#A78BFA] mt-2 text-center font-bold">
          3 months for $9.99
        </p>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
 *  PlanPickerDialog — Standard / Premium picker
 * ─────────────────────────────────────────────────────────── */
export function PlanPickerDialog({ open, onClose, planStatus, onPicked }) {
  const { getAuthHeaders } = useAuth();
  const [picked, setPicked] = useState('standard');
  const [claimPromo, setClaimPromo] = useState(true);
  const [busy, setBusy] = useState(false);
  if (!open || !planStatus) return null;

  const promo = planStatus.promo;
  const plans = planStatus.plans || [];

  const confirm = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/builder/select-plan`,
        { plan_id: picked, claim_promo: !!(claimPromo && promo?.eligible) },
        { headers: getAuthHeaders() }
      );
      toast.success("You're on the " + (plans.find(p => p.id === picked)?.name || '') + ' plan!');
      onPicked?.();
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save plan');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[210] bg-black/85 backdrop-blur flex items-center justify-center p-4" data-testid="plan-picker">
      <div className="relative max-w-3xl w-full bg-gradient-to-br from-[#15152E] to-[#0E0E23] border border-white/[0.08] rounded-2xl p-7 shadow-[0_30px_80px_rgba(0,0,0,0.8)]">
        <button onClick={onClose} className="absolute top-4 right-4 text-white/55 hover:text-white p-1" data-testid="plan-picker-close">
          <X size={18} />
        </button>
        <p className="text-[10px] uppercase tracking-[0.22em] text-[#A78BFA] font-bold mb-2">Pick your plan</p>
        <h3 className="text-2xl font-bold text-white font-syne tracking-tight">
          Choose how you'll grow on EntrepreNatives
        </h3>
        <p className="text-sm text-white/55 mt-1.5">
          Build for free. Pay when you publish. {promo?.eligible && <span className="text-amber-300 font-bold">Get your first 3 months for just $9.99.</span>}
        </p>

        {/* Plan cards */}
        <div className="grid sm:grid-cols-2 gap-4 mt-6">
          {plans.map((p) => {
            const active = picked === p.id;
            const isPremium = p.id === 'premium';
            return (
              <button
                key={p.id}
                onClick={() => setPicked(p.id)}
                className={`relative text-left rounded-2xl p-6 border transition-all ${
                  active
                    ? (isPremium
                      ? 'border-amber-300 bg-amber-400/10 shadow-[0_0_28px_rgba(245,158,11,0.25)]'
                      : 'border-[#A78BFA] bg-[#A78BFA]/10 shadow-[0_0_28px_rgba(167,139,250,0.25)]')
                    : 'border-white/[0.08] bg-white/[0.025] hover:border-white/30 hover:bg-white/[0.05]'
                }`}
                data-testid={`plan-card-${p.id}`}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="inline-flex items-center gap-2">
                    {isPremium
                      ? <Crown size={16} className="text-amber-300" />
                      : <Zap size={16} className="text-[#A78BFA]" />}
                    <span className={`text-[10px] uppercase tracking-[0.18em] font-bold ${isPremium ? 'text-amber-300' : 'text-[#A78BFA]'}`}>
                      {p.name}
                    </span>
                  </div>
                  {active && (
                    <span className="w-5 h-5 rounded-full flex items-center justify-center" style={{
                      background: isPremium ? 'linear-gradient(135deg, #F59E0B, #D97706)' : 'linear-gradient(135deg, #8B5CF6, #6D28D9)',
                    }}>
                      <Check size={11} className="text-white" strokeWidth={3} />
                    </span>
                  )}
                </div>
                <p className="text-3xl font-bold text-white font-syne tabular-nums">
                  ${p.monthly_price}<span className="text-sm font-medium text-white/55 ml-1">/mo</span>
                </p>
                {promo?.eligible && (
                  <p className="text-xs text-amber-300 font-bold mt-1">
                    🎁 First 3 months: $9.99 total
                  </p>
                )}
                <ul className="mt-4 space-y-1.5">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-[12px] text-white/75">
                      <Check size={12} className={`mt-0.5 shrink-0 ${isPremium ? 'text-amber-300' : 'text-[#A78BFA]'}`} />
                      {f}
                    </li>
                  ))}
                </ul>
              </button>
            );
          })}
        </div>

        {/* Promo opt-in */}
        {promo?.eligible && (
          <label className="flex items-center gap-3 mt-5 p-3 rounded-xl bg-amber-500/8 border border-amber-400/20 cursor-pointer">
            <input
              type="checkbox"
              checked={claimPromo}
              onChange={(e) => setClaimPromo(e.target.checked)}
              className="accent-amber-400 w-4 h-4"
              data-testid="claim-promo"
            />
            <span className="text-xs text-white/85">
              Claim the <span className="font-bold text-amber-300">3 months for $9.99</span> intro offer. After that, your selected plan kicks in at full price.
            </span>
          </label>
        )}

        <div className="flex items-center justify-between gap-3 mt-6">
          <p className="text-[10px] text-white/45 max-w-sm">
            No card needed today. You'll be billed only when you publish your site and your trial ends.
          </p>
          <button
            onClick={confirm}
            disabled={busy}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-[#8B5CF6] to-[#6D28D9] text-white text-xs font-bold uppercase tracking-wider shadow-[0_12px_30px_rgba(139,92,246,0.4)] hover:shadow-[0_12px_42px_rgba(139,92,246,0.55)] disabled:opacity-40 transition-all"
            data-testid="plan-picker-confirm"
          >
            {busy ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
            Lock it in
          </button>
        </div>
      </div>
    </div>
  );
}
